import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleButton, withStyles } from 'vopay';
import { BodyText, CopyButton, TruncatedText } from 'UI';
import type { AccountCredentials } from './accountCredentials';
import styles from './CredentialsStrip.module.scss';

const cn = withStyles(styles);

const MASKED_SECRET = '••••••••••••';

type CredentialTileProps = {
  label: string;
  value: string;
  testId: string;
  /** Masked by default with a show / hide toggle; for the secret. */
  sensitive?: boolean;
};

function CredentialTile({ label, value, testId, sensitive = false }: CredentialTileProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountHeaderCards.CredentialsStrip' });
  const [isRevealed, setIsRevealed] = useState(false);

  const isMasked = sensitive && !isRevealed;

  return (
    <div className={cn('credentialTile')}>
      <BodyText size="sm" weight="md">
        {label}
      </BodyText>

      <div className={cn('credentialValue')}>
        <BodyText className={cn('credentialText')} data-testid={testId}>
          {/* No explicit `information`: the tooltip falls back to what is shown, so a masked secret
              stays masked on hover rather than being revealed by it. */}
          <TruncatedText>{isMasked ? MASKED_SECRET : value}</TruncatedText>
        </BodyText>

        {sensitive && (
          <CircleButton
            icon={isRevealed ? 'visibility_off' : 'visibility'}
            colorScheme="action"
            aria-label={
              isRevealed
                ? t('hideSecret', { defaultValue: 'Hide shared secret' })
                : t('showSecret', { defaultValue: 'Show shared secret' })
            }
            aria-pressed={isRevealed}
            onClick={() => setIsRevealed((revealed) => !revealed)}
          />
        )}

        <CopyButton content={value} variant="circle" />
      </div>
    </div>
  );
}

type CredentialsStripProps = {
  credentials: AccountCredentials;
};

/**
 * The API credentials strip along the bottom of an entity's header card (spec 5.5): Account ID,
 * API Key and the secret, each with a copy button, the secret masked until revealed. Shared by the
 * account card and the partner card; the caller decides who may see it and when (Active only).
 */
export default function CredentialsStrip({ credentials }: CredentialsStripProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountHeaderCards.CredentialsStrip' });

  return (
    <div className={cn('credentials')} data-testid="accountCredentials">
      <CredentialTile
        label={t('accountId', { defaultValue: 'Account ID' })}
        value={credentials.accountId}
        testId="credentialAccountId"
      />

      <CredentialTile
        label={t('apiKey', { defaultValue: 'API Key' })}
        value={credentials.apiKey}
        testId="credentialApiKey"
      />

      <CredentialTile
        label={
          credentials.secretKind === 'sharedSecret'
            ? t('apiSharedSecret', { defaultValue: 'API Shared Secret' })
            : t('apiSignature', { defaultValue: 'API Signature' })
        }
        value={credentials.secret}
        testId="credentialSecret"
        sensitive
      />
    </div>
  );
}
